import React, { useEffect, useRef, useState } from 'react';
import { Input, Message, Popover } from '@meitu/saas-ui';
import type { Editor } from '@meitu/saas-editor-core';
import { trackConfig, type EventEmitter } from '@meitu/saas-editor-core/es/framework';
import { IExtraInfo, ILayerProps, MAX_STAGES, MAX_STAGES_TIP } from '@meitu/saas-editor-core/es/version-types';
import { getLocalIdentity } from '@meitu/xdesign-identity';
import { getEnterpriseID } from '@meitu/xdesign-widgets';
import GenBtn, { GenRef } from '@meitu/xdesign-widgets/dist/meidouInfo/compoments/genBtn';
import { getTransferId } from '@meitu/xdesign-widgets/dist/meidouInfo/utils';
import { useLocaleStore } from '@/i18n';
import { useGeneratePPT } from '../../hooks/use-generate-ppt';
import { CloseBtn } from '../close-btn';
import { ModelTitle } from '../model-title';

const BTN_UUID = 'create_single_page_submit';

type CreateAiPPTProps = {
    editor: Editor;
    events: EventEmitter;
    isAiPPT: boolean;
    recordId: string;
    setIsAiCreate: (isAiCreate: boolean) => void;
    setAiRegen: (data: { isAiRegen: boolean }) => void;
    isAiCreate: boolean;
    getCurrentScene: () => string;
    isFromHuaweiCustom: () => boolean;
    transferIdParams: any;
    // 从旧代码 const isFullscreen = useIsFullscreenState(); 搬迁
    isFullscreen: boolean;
    // 从旧代码 const subject = useSubjectState(); 搬迁
    templateSubject: string;
    initPPT: (stageIndex?: number) => void;
    // import { useCreateTipHelper } from '@/editor/components/pptCreateTip';
    // const createTipHelper = useCreateTipHelper(); createTipHelper.show()
    showCreateTip: (tip: string) => void;
    // import { useCreateTipHelper } from '@/editor/components/pptCreateTip';
    // const createTipHelper = useCreateTipHelper(); createTipHelper.hide()
    hideCreateTip: () => void;
    // import { transferTemplateToClient } from '@/hooks/useFetchTemplate/initTemplate';
    transferTemplateToClient: (templateDetail: any, fontList: any) => Promise<any>;
    // import { packageTemplateConf } from '@/hooks/useFetchTemplate';
    packageTemplateConf: (
        templateConf: any,
        extraInfo: IExtraInfo,
        init: boolean,
    ) => {
        stages: ILayerProps[][];
        extraInfos: IExtraInfo[];
    };
    // import { fetchPPTApplyItem } from '@/services';
    fetchPPTApplyItem: (params: any) => Promise<any>;
    // import { fetchPPTCheckItemProcess } from '@/services';
    fetchPPTCheckItemProcess: (params: any) => Promise<any>;
    // import { fetchPPTItemDetail } from '@/services';
    fetchPPTItemDetail: (params: any) => Promise<any>;
};

// ai生成单页
export const CreateAiPPT: React.FC<CreateAiPPTProps> = React.memo(
    ({
        editor,
        events,
        isAiPPT,
        recordId,
        setIsAiCreate,
        setAiRegen,
        isAiCreate,
        getCurrentScene,
        isFromHuaweiCustom,
        transferIdParams,
        isFullscreen,
        initPPT,
        showCreateTip,
        hideCreateTip,
        transferTemplateToClient,
        templateSubject,
        fetchPPTApplyItem,
        fetchPPTCheckItemProcess,
        fetchPPTItemDetail,
        packageTemplateConf,
    }) => {
        const { $t } = useLocaleStore();

        const [prompt, setPrompt] = useState('');
        const [needUpdate, setNeedUpdate] = useState(true);
        const priceBtn = useRef<GenRef>(null);

        const generatePPT = useGeneratePPT({
            editor,
            events,
            prompt,
            setPrompt,
            tip: $t((v) => v.collapse.navigationCollapse.ppt.tips.singlePptGenerating),
            btnUuid: BTN_UUID,
            needUpdate: needUpdate && isAiCreate,
            setNeedUpdate,
            recordId,
            setIsAiCreate,
            setAiRegen,
            showCreateTip,
            hideCreateTip,
            transferTemplateToClient,
            templateSubject,
            fetchPPTApplyItem,
            fetchPPTCheckItemProcess,
            fetchPPTItemDetail,
            packageTemplateConf,
        });

        const closePrompt = () => {
            setIsAiCreate(false);
            setPrompt('');
        };
        // todo
        // useDownloadEvent(closePrompt);

        useEffect(() => {
            if (isFullscreen) {
                closePrompt();
            }
        }, [isFullscreen]);

        const submitHandle = async () => {
            const transferId = await getTransferId({
                source: getCurrentScene(),
                oid: getLocalIdentity()?.oid || '',
                user_mode: getEnterpriseID() ? '1' : '',
                ...transferIdParams,
            });
            const index = editor.stage.count();
            if (index < MAX_STAGES) {
                generatePPT.submit({
                    click() {
                        window._stat_track('create_single_page_submit');
                    },
                    fail() {
                        window._stat_track('create_single_page_result', {
                            result: 0,
                        });
                    },
                    success(stages: ILayerProps[][], extraInfos: IExtraInfo[]) {
                        window._stat_track('create_single_page_result', {
                            result: 1,
                        });
                        Message.success($t((v) => v.collapse.navigationCollapse.ppt.tips.generateSuccess));
                        console.log('templateDetail 22', stages, extraInfos);
                        editor.stage.insertToNext(stages[0], extraInfos[0], false, trackConfig.page.createSingle);
                        initPPT(editor.active.getActiveStageIndex());
                    },
                    transferId,
                });
            } else {
                Message.warning(MAX_STAGES_TIP());
            }
            window._stat_track('tpbj_subfunction_tick', {
                func: '19_94',
            });
        };

        // 点击前校验回调
        const handleBeforeGen = (): boolean => {
            return !!prompt;
        };

        // 错误提示回调
        const handleErrorMessage = (msg: string) => {
            Message.error(msg);
        };

        if (!isAiPPT) return null;

        return (
            <>
                <Popover
                    placement="bottom"
                    overlayClassName="c-navigation-modal"
                    content={
                        <>
                            <CloseBtn
                                onClick={() => {
                                    setIsAiCreate(false);
                                    editor.enableOperate();
                                }}
                            />
                            <div className="c-ai-content">
                                <ModelTitle left={$t((v) => v.collapse.navigationCollapse.ppt.currentSubject)} />
                                <div className="c-ai-content__subject">{templateSubject}</div>
                                <ModelTitle left={$t((v) => v.collapse.navigationCollapse.ppt.prompt)} />
                                <div className="c-ai-content__input">
                                    <Input.TextArea
                                        style={{ resize: 'none', fontSize: 14 }}
                                        className="input-text theme"
                                        maxLength={300}
                                        rows={8}
                                        value={prompt}
                                        autoFocus={true}
                                        onChange={({ target: { value } }) => {
                                            setPrompt(value.trim());
                                        }}
                                        onKeyDown={({ keyCode }: any) => {
                                            if (keyCode === 13) {
                                                priceBtn.current?.onClick();
                                            }
                                        }}
                                        placeholder={$t((v, { r }) =>
                                            r(v.collapse.navigationCollapse.ppt.placeHolder, [templateSubject]),
                                        )}
                                        onClick={() => {}}
                                    />
                                </div>
                                <GenBtn
                                    ref={priceBtn}
                                    uuid={BTN_UUID}
                                    isFree={isFromHuaweiCustom()}
                                    className="c-navigation-collapse__btn-aippt"
                                    text={$t((v) => v.collapse.navigationCollapse.submit)}
                                    onBeforeGen={handleBeforeGen}
                                    onGen={submitHandle}
                                    onErrorMessage={handleErrorMessage}
                                    source={getCurrentScene()}
                                />
                            </div>
                        </>
                    }
                    destroyTooltipOnHide
                    open={isAiCreate}
                >
                    <div className="c-navigation-modal-anchor"></div>
                </Popover>
            </>
        );
    },
);
